import { TagType } from '@/_models/Tag';
import { PolygonActionType } from '@/_models/virtualStaging/Polygon';
import { TourActionType } from '@/_models/virtualStaging/VSObject';
import * as all from '@/components/util/all';
import isValidUrl from '@/utils/isValidUrl';
import {
  ExportAudio,
  MusicObj,
  MusicObjectKey,
  TMusicData,
  TMusicDataEntry,
  TPlaylist,
} from './types';
import { emitter, MittEvents } from '../../events';

export { MusicObj, MusicObjectKey };

export class AudioModule {
  //@ts-ignore
  private musicData: TMusicData = {};

  private videosForSoundControl = [];

  private volumeValue = 1;

  private isAllMusicMute = false;

  private lowestPriotiryLevel = 10;

  private nowPlayingPriorityLevel;

  private playList: TPlaylist[] = [];

  private isGreenScreenPlaying = false;

  private repeatCount = 3;

  private isTourAudioPaused = true;

  private eventDebounceId;

  private static instance: AudioModule;

  public static getInstance(): AudioModule {
    if (!AudioModule.instance) {
      AudioModule.instance = new AudioModule();
    }

    return AudioModule.instance;
  }

  private removeAudioFromPlayList = (type: MusicObjectKey, id: string | number): void => {
    this.playList = this.playList.filter((el) => el.id !== `${id}`);
  };

  private async nextMusic(skipTourMusic = false, setPriorityLevel = false): Promise<void> {
    if (this.isGreenScreenPlaying || !this.playList.length) {
      if (setPriorityLevel) {
        this.nowPlayingPriorityLevel = this.lowestPriotiryLevel;
      }
      return;
    }

    try {
      const firstMusicIsTour = this.playList[0]?.type.name === MusicObjectKey.TOUR;
      const music = skipTourMusic && firstMusicIsTour ? this.playList[1] : this.playList[0];

      if (!music) return;

      await this.repeatNextMusicCall(music.audio);

      if (music.type.name === MusicObjectKey.TOUR) {
        this.isTourAudioPaused = false;
        const isMusicTourPlaying = !music.audio.paused;
        if (isMusicTourPlaying) {
          emitter.emit(MittEvents.TOUR_AUDIO_PLAY, { isTourMusic: true });
        }
      }

      this.nowPlayingPriorityLevel = music.priority;
    } catch (err) {
      console.error(`Can't play next music. Message: ${err}`);
      this.playList.shift();
      await this.nextMusic();
    }
  }

  private removeDuplicatedAudio = (id: string, type?: MusicObjectKey): void => {
    this.playList
      .filter((audio) => type === audio.type.name || id === audio.id)
      .forEach(({ audio }) => audio.pause());
    this.playList = this.playList.filter(
      (audio) => audio.id !== id && (!type || type !== audio.type.name),
    );
  };

  private sleep = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  private repeatMusicCall = async (type: MusicObjectKey, objectId: string): Promise<void> => {
    await this.startPlay(this.getAudio(type, objectId), 0);
  };

  private repeatNextMusicCall = async (nextAudio: HTMLAudioElement): Promise<void> => {
    await this.startPlay(nextAudio, 0);
  };

  private setVSAudiosVolume = (value: number) => {
    this.videosForSoundControl?.forEach((videoNode) => {
      const { video } = videoNode;
      if (value === 0) {
        video.muted = true;
      } else {
        video.volume = value;
        if (!videoNode?.muteBtn?.active) {
          video.muted = false;
        }
      }
    });
  };

  private startPlay = async (audio: HTMLAudioElement, index: number) => {
    if (index >= this.repeatCount) return;
    try {
      // Pause currently playing audio before playing new one
      this.pauseAllMusic();
      // Mute VS Audios if any are playing
      this.setVSAudiosVolume(0);
      audio.muted = this.isAllMusicMute || this.volumeValue === 0;
      audio?.play();
    } catch (e) {
      await this.sleep(500);
      await this.startPlay(audio, index + 1);
      console.log('NOT PLAYING!');
    }
  };

  private setTourMusic = (value: boolean) => {
    this.isTourAudioPaused = value;
  };

  private isPlaying = (id): boolean => {
    return this.playList.some((i) => i.id === `${id}` && !i.audio.paused);
  };

  getVolume = () => {
    return this.volumeValue;
  };

  getAllMusicData = () => {
    return this.musicData;
  };

  isTourMusicPaused = () => {
    return this.isTourAudioPaused;
  };

  getAudioByType = (type: MusicObjectKey): TMusicDataEntry => {
    return this.musicData?.[type];
  };

  getAudio = (type: MusicObjectKey, id: number | string): HTMLAudioElement | undefined => {
    return this.musicData?.[type]?.[`${id}`];
  };

  getTourMusic = () => {
    return this.playList.find((el) => el.type.name === MusicObjectKey.TOUR);
  };

  setGreenScreenPlaying = (value: boolean) => {
    this.isGreenScreenPlaying = value;
  };

  stopTourMusic = async () => {
    await this.stopAudioByType(MusicObjectKey.TOUR, false);
    await this.stopAudioByType(MusicObjectKey.FLOORS, false);
    this.setTourMusic(true);
  };

  changeSelectedAudioLoop = (type: MusicObjectKey, id: number | string, value: boolean) => {
    this.getAudio(type, id).loop = value;
  };

  changeSelectedAudioVolume = (type: MusicObjectKey, id: number | string, value: number) => {
    this.getAudio(type, id).volume = value;
  };

  getFirstMusic = (): TPlaylist => {
    return this.playList[0];
  };

  resumeMusic = async (): Promise<void> => {
    await this.nextMusic();
  };

  pauseAudio = async (id: number | string): Promise<void> => {
    const audio = this.playList.find((a) => a.id === `${id}`)?.audio;
    audio?.pause();
    if (this.playList.length > 1) {
      this.playList.push(this.playList.shift());
      await this.nextMusic();
    }
  };

  resumeAudio = async (id: number | string): Promise<void> => {
    this.getFirstMusic().audio?.pause();
    this.playList.find((audio) => audio.id === `${id}`)?.audio?.play();
  };

  stopAudioByType = async (type: MusicObjectKey, removeFromPlaylist = true): Promise<void> => {
    this.setVSAudiosVolume(this.volumeValue);

    this.playList.filter((el) => el.type.name === type).forEach(({ audio }) => audio.pause());
    if (removeFromPlaylist) {
      this.playList = this.playList.filter((el) => el.type.name !== type);
    }

    const skipTourMusic = type !== MusicObjectKey.SWEEPGROUPS;
    await this.nextMusic(skipTourMusic);
  };

  pauseAllMusic = (overridePriority = true): void => {
    this.playList.forEach((i) => {
      if (
        !i.audio.paused &&
        (i.type.name === MusicObjectKey.TOUR || i.type.name === MusicObjectKey.FLOORS)
      ) {
        emitter.emit(MittEvents.TOUR_AUDIO_PLAY, { isTourMusic: false });
      }

      i.audio.pause();
    });
    if (overridePriority) {
      this.nowPlayingPriorityLevel = this.lowestPriotiryLevel;
    }
  };

  addMusicToVolumeControl = (el) => {
    this.videosForSoundControl.push(el);
  };

  allPlayingVideosAreMuted = () => {
    return !this.videosForSoundControl.some(
      (el) => !el.video.paused && el.video.volume > 0 && !el.video.muted,
    );
  };

  muteHandler = (state: boolean): void => {
    this.isAllMusicMute = state;
    Object.values(this.musicData).forEach((data) => {
      Object.values(data).forEach((el) => {
        el.muted = state;
      });
    });
  };

  volumeHandler = (value: number): void => {
    if (!value && value !== 0) return;

    this.volumeValue = value;

    this.setVSAudiosVolume(value);

    Object.values(this.musicData).forEach((data) => {
      Object.values(data).forEach((el) => {
        if (value === 0) {
          el.muted = true;
        } else {
          el.volume = value;
          el.muted = false;
        }
      });
    });

    clearTimeout(this.eventDebounceId);

    this.eventDebounceId = setTimeout(() => {
      emitter.emit(MittEvents.AUDIO_VOLUME, { volumeValue: this.volumeValue });
    }, 500);
  };

  tourHasAudio = (): boolean => {
    return Object.keys(this.musicData).length > 0 || this.videosForSoundControl.length > 0;
  };

  clearPlayListQueue = (): void => {
    this.playList.forEach((el) => {
      if (el.type.name !== MusicObjectKey.TOUR && el.type.name !== MusicObjectKey.FLOORS) {
        const audio = this.getAudio(el.type.name, el.id);
        if (!audio) return;
        audio.pause();
        audio.currentTime = 0;
        this.removeAudioFromPlayList(el?.type?.name, el?.id);
      }
    });
  };

  playAudio = async (data: ExportAudio): Promise<void> => {
    const {
      type: { name: type, priority },
      id,
    } = data;

    if (!this.getAudio(type, id) || this.isPlaying(id)) {
      return;
    }
    if (type === MusicObjectKey.TOUR) {
      const audios = this.getAudioByType(type);
      const audioIdsForOtherTours = Object.keys(audios).filter((audioId) => audioId !== id);
      audioIdsForOtherTours.forEach((audioId) =>
        this.removeAudioFromPlayList(MusicObjectKey.TOUR, audioId),
      );
    }
    const playListAudio = {
      id: `${id}`,
      audio: this.getAudio(type, id),
      priority,
      type: data.type,
    };
    if (this.nowPlayingPriorityLevel >= priority || !this.nowPlayingPriorityLevel) {
      try {
        const isTourMusic = type === MusicObjectKey.TOUR || type === MusicObjectKey.FLOORS;
        emitter.emit(MittEvents.TOUR_AUDIO_PLAY, { isTourMusic });

        if (isTourMusic) {
          this.isTourAudioPaused = false;
        }
        this.nowPlayingPriorityLevel = priority;
        if (type === MusicObjectKey.SWEEPGROUPS) {
          this.stopTourMusic();
          this.removeDuplicatedAudio(playListAudio.id, MusicObjectKey.SWEEPGROUPS);
        } else {
          this.removeDuplicatedAudio(playListAudio.id);
        }
        this.startPlay(this.getAudio(type, id), 0);

        this.clearPlayListQueue();
        this.playList.unshift(playListAudio);
      } catch (err) {
        await this.repeatMusicCall(type, `${id}`);
        if (!this.getAudio(type, id).paused) {
          console.error(`Can't play ${type} audio. Message: ${err}`);
        } else {
          this.pauseAllMusic();
        }
      }
      return;
    }
    const index = this.playList.findIndex((el) => el.priority > priority);
    this.removeDuplicatedAudio(playListAudio.id);
    if (index) {
      this.playList.splice(index - 1, 0, playListAudio);
    } else {
      this.playList.push(playListAudio);
    }
  };

  private urlHandling = (type: MusicObjectKey, data: Record<string, unknown>): string => {
    let src: string;
    const awsUrl = process.env.NEXT_PUBLIC_CDN;
    switch (type) {
      case MusicObjectKey.POLYGONS:
        if (data.url && data.actionType === PolygonActionType.Music) {
          src = `${awsUrl}/tours/${data.tourId}/polygons/${data.url}`;
        }
        break;
      case MusicObjectKey.PANORAMAS:
        if (data.url) {
          src = `${awsUrl}/tours/${data.tourId}/panoramas/${data.panoramaUrl}/${data.url}`;
        }
        break;
      case MusicObjectKey.TAGS:
        if (data.url && data.type === TagType.Music) {
          src = `${awsUrl}/tours/${data.tourId}/tags/${data.url}`;
        }
        break;
      case MusicObjectKey.TOUR:
        src = `${awsUrl}/tours/${data.id}/music.mp3${all.getVersion(data.updatedAt)}`;
        break;
      case MusicObjectKey.FLOORS:
        if (data.url) {
          src = `${data.url}`;
        }
        break;
      case MusicObjectKey.SWEEPGROUPS:
        if (data.music) {
          src = `${awsUrl}/tours/${data.tourId}/sweeps/${data.music}`;
        }
        break;
      case MusicObjectKey.VSIMAGES:
        if (data.actionURL && data.actionType === TourActionType.Music) {
          if (data.actionURL.toString().includes('http')) {
            src = data.actionURL.toString();
          } else {
            src = `${awsUrl}/tours/${data.tourId}/vsimages/${data.actionURL}`;
          }
        }
        break;
      case MusicObjectKey.BUTTON:
        if (data.buttonSoundUrl) {
          src = `${awsUrl}/tours/${data.tourId}/game/${data.id}/${data.buttonSoundUrl}`;
        }
        break;
      case MusicObjectKey.GAME_SELECTED_AUDIO:
        if (data.sound) {
          src = `${process.env.NEXT_PUBLIC_CDN}/games/${data.gameId}/locks/${data.id}/${data.sound}`;
        }
        break;
      default:
        break;
    }
    return src;
  };

  private createAudioStream = async (type: MusicObjectKey, data: Record<string, unknown>) => {
    const objectId = data.id.toString();
    if (this.getAudio(type, objectId)) return;
    const src = this.urlHandling(type, data);
    const isValid = await isValidUrl(src);

    if (isValid) {
      const audioObject = new Audio(src);
      audioObject.autoplay = false;
      audioObject.muted = this.isAllMusicMute;
      audioObject.volume = this.volumeValue;

      if (type !== MusicObjectKey.TAGS && type !== MusicObjectKey.PANORAMAS) {
        audioObject.addEventListener('ended', async () => {
          audioObject.currentTime = 0;
          this.removeAudioFromPlayList(type, objectId);
          await this.nextMusic(false, true);
        });
      }

      this.musicData[type] = { ...this.getAudioByType(type), [objectId]: audioObject };
    }
  };

  importAudioData = async (type: MusicObjectKey, data: Record<string, unknown>[]) => {
    const promises = data?.map((element) => this.createAudioStream(type, element)).filter(Boolean);
    if (promises) return Promise.all(promises);
    return Promise.resolve();
  };
}
